import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

type AttentionReportsInsert = Database['public']['Tables']['attention_reports']['Insert'];

export interface ArticleMetrics {
  interviewId: string;
  title: string;
  totalSessions: number;
  totalAttentionSeconds: number;
  averageAttentionSeconds: number;
  totalTAV: number;
  averageTAV: number;
  averageScrollDepth: number;
  expandClicks: number;
  kaiMessages: number;
  kaiSeconds: number;
  quizCompletions: number;
  pollAnswers: number;
  debateParticipants: number;
  tabVisits: Record<string, number>;
  zoneVisibility: Record<string, number>;
}

export interface HeatmapData {
  zoneId: string;
  label: string;
  averageSeconds: number;
  totalSeconds: number;
  percentOfTotal: number;
  intensity: 'low' | 'medium' | 'high';
}

export interface KaiInsight {
  queryText: string;
  category: string | null;
  count: number;
}

export interface AttentionReport {
  id: string;
  name: string;
  interviewIds: string[];
  dateRangeStart: string;
  dateRangeEnd: string;
  metrics: {
    totalReaders: number;
    averageAttentionSeconds: number;
    totalAttentionHours: number;
    averageTAV: number;
    comparisonMultiplier: number; // vs Facebook 1.7 seconds
    engagementDistribution: {
      passive: number;
      kai: number;
      quiz: number;
      debate: number;
      poll: number;
    };
  };
  heatmapData: HeatmapData[];
  kaiInsights: KaiInsight[];
  articleBreakdown: ArticleMetrics[];
  createdAt: string;
}

const FACEBOOK_AVG_ATTENTION_SECONDS = 1.7;

/**
 * Get aggregated metrics for selected articles within a date range
 */
export async function getArticleMetrics(
  interviewIds: string[],
  startDate: Date,
  endDate: Date
): Promise<ArticleMetrics[]> {
  try {
    const { data: metricsData, error: metricsError } = await supabase
      .from('attention_metrics')
      .select('*')
      .in('interview_id', interviewIds)
      .gte('created_at', startDate.toISOString())
      .lte('created_at', endDate.toISOString());

    if (metricsError) {
      console.error('Error fetching attention metrics:', metricsError);
      return [];
    }

    // Get interview titles
    const { data: interviews, error: interviewError } = await supabase
      .from('interviews')
      .select('id, title')
      .in('id', interviewIds);

    if (interviewError) {
      console.error('Error fetching interviews:', interviewError);
    }

    const titleMap = new Map(interviews?.map(i => [i.id, i.title]) || []);

    // Aggregate by interview
    const aggregated = new Map<string, {
      sessions: Set<string>;
      totalAttention: number;
      totalTAV: number;
      scrollDepths: number[];
      expandClicks: number;
      kaiMessages: number;
      kaiSeconds: number;
      quizCompletions: number;
      pollAnswers: number;
      debateParticipants: Set<string>;
      tabVisits: Record<string, number>;
      zoneVisibility: Record<string, number>;
    }>();

    metricsData?.forEach((m) => {
      const existing = aggregated.get(m.interview_id) || {
        sessions: new Set<string>(),
        totalAttention: 0,
        totalTAV: 0,
        scrollDepths: [],
        expandClicks: 0,
        kaiMessages: 0,
        kaiSeconds: 0,
        quizCompletions: 0,
        pollAnswers: 0,
        debateParticipants: new Set<string>(),
        tabVisits: {},
        zoneVisibility: {},
      };

      existing.sessions.add(m.session_id);
      existing.totalAttention += m.passive_seconds + m.kai_seconds + m.quiz_seconds + m.debate_seconds;
      existing.totalTAV += m.total_attention_value;
      existing.scrollDepths.push(m.scroll_depth_percent);
      existing.expandClicks += m.expand_clicks;
      existing.kaiMessages += m.kai_messages;
      existing.kaiSeconds += m.kai_seconds;
      if (m.quiz_completed) existing.quizCompletions += 1;
      if (m.poll_answered) existing.pollAnswers += 1;
      if (m.debate_arguments > 0 || m.debate_votes > 0) {
        existing.debateParticipants.add(m.session_id);
      }

      // Merge tab visits
      const tabVisits = m.tab_visits as Record<string, number> || {};
      Object.entries(tabVisits).forEach(([tab, count]) => {
        existing.tabVisits[tab] = (existing.tabVisits[tab] || 0) + count;
      });

      // Merge zone visibility
      const zoneVis = m.zone_visibility as Record<string, number> || {};
      Object.entries(zoneVis).forEach(([zone, seconds]) => {
        existing.zoneVisibility[zone] = (existing.zoneVisibility[zone] || 0) + seconds;
      });

      aggregated.set(m.interview_id, existing);
    });

    return Array.from(aggregated.entries()).map(([interviewId, data]) => ({
      interviewId,
      title: titleMap.get(interviewId) || 'Ukjent artikkel',
      totalSessions: data.sessions.size,
      totalAttentionSeconds: data.totalAttention,
      averageAttentionSeconds: data.sessions.size > 0 ? data.totalAttention / data.sessions.size : 0,
      totalTAV: data.totalTAV,
      averageTAV: data.sessions.size > 0 ? data.totalTAV / data.sessions.size : 0,
      averageScrollDepth: data.scrollDepths.length > 0 
        ? data.scrollDepths.reduce((a, b) => a + b, 0) / data.scrollDepths.length 
        : 0,
      expandClicks: data.expandClicks,
      kaiMessages: data.kaiMessages,
      kaiSeconds: data.kaiSeconds,
      quizCompletions: data.quizCompletions,
      pollAnswers: data.pollAnswers,
      debateParticipants: data.debateParticipants.size,
      tabVisits: data.tabVisits,
      zoneVisibility: data.zoneVisibility,
    }));
  } catch (error) {
    console.error('Error in getArticleMetrics:', error);
    return [];
  }
}

/**
 * Generate heatmap data from zone visibility metrics
 */
export function generateHeatmapData(metrics: ArticleMetrics[]): HeatmapData[] {
  const combinedZones: Record<string, number> = {};

  metrics.forEach(m => {
    Object.entries(m.zoneVisibility).forEach(([zone, seconds]) => {
      combinedZones[zone] = (combinedZones[zone] || 0) + seconds;
    });
  });

  const totalSeconds = Object.values(combinedZones).reduce((a, b) => a + b, 0);
  const maxSeconds = Math.max(...Object.values(combinedZones), 1);

  const zoneLabels: Record<string, string> = {
    'context': 'Innledning/Kontekst',
    'question': 'Spørsmål',
    'answer': 'Svar',
    'expert-intro': 'Ekspert-intro',
    'hashtags': 'Hashtags',
    'ai-summary': 'AI-oppsummering',
    'kai-chat': 'Kai Chat',
    'quiz': 'Quiz',
    'poll': 'Poll',
    'debate': 'Debatt',
    'innspill': 'Innspill',
  };

  return Object.entries(combinedZones)
    .map(([zoneId, seconds]) => {
      const percentOfMax = seconds / maxSeconds;
      let intensity: 'low' | 'medium' | 'high';
      if (percentOfMax > 0.66) intensity = 'high';
      else if (percentOfMax > 0.33) intensity = 'medium';
      else intensity = 'low';

      return {
        zoneId,
        label: zoneLabels[zoneId] || zoneId,
        averageSeconds: metrics.length > 0 ? seconds / metrics.length : 0,
        totalSeconds: seconds,
        percentOfTotal: totalSeconds > 0 ? (seconds / totalSeconds) * 100 : 0,
        intensity,
      };
    })
    .sort((a, b) => b.totalSeconds - a.totalSeconds);
}

/**
 * Get Kai insights (most common questions)
 */
export async function getKaiInsights(
  interviewIds: string[],
  startDate: Date,
  endDate: Date,
  limit: number = 10
): Promise<KaiInsight[]> {
  try {
    const { data, error } = await supabase
      .from('kai_insights')
      .select('query_text, query_category')
      .in('interview_id', interviewIds)
      .gte('created_at', startDate.toISOString())
      .lte('created_at', endDate.toISOString());

    if (error) {
      console.error('Error fetching Kai insights:', error);
      return [];
    }

    // Group and count
    const counts = new Map<string, { category: string | null; count: number }>();
    data?.forEach(d => {
      // Normalize query text (lowercase, trim)
      const normalized = d.query_text.toLowerCase().trim();
      const existing = counts.get(normalized);
      if (existing) {
        existing.count += 1;
      } else {
        counts.set(normalized, { category: d.query_category, count: 1 });
      }
    });

    return Array.from(counts.entries())
      .map(([queryText, { category, count }]) => ({
        queryText,
        category,
        count,
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, limit);
  } catch (error) {
    console.error('Error in getKaiInsights:', error);
    return [];
  }
}

/**
 * Calculate comparison multiplier vs industry standard (Facebook 1.7 seconds)
 */
export function calculateComparisonMultiplier(averageSeconds: number): number {
  return averageSeconds / FACEBOOK_AVG_ATTENTION_SECONDS;
}

/**
 * Generate a complete attention report
 */
export async function generateAttentionReport(
  name: string,
  interviewIds: string[],
  startDate: Date,
  endDate: Date
): Promise<AttentionReport | null> {
  try {
    const [articleMetrics, kaiInsights] = await Promise.all([
      getArticleMetrics(interviewIds, startDate, endDate),
      getKaiInsights(interviewIds, startDate, endDate),
    ]);

    // Calculate totals
    const totalReaders = new Set(articleMetrics.flatMap(m => m.totalSessions)).size || 
      articleMetrics.reduce((sum, m) => sum + m.totalSessions, 0);
    
    const totalAttentionSeconds = articleMetrics.reduce((sum, m) => sum + m.totalAttentionSeconds, 0);
    const totalKaiSeconds = articleMetrics.reduce((sum, m) => sum + m.kaiSeconds, 0);
    const totalQuizSeconds = articleMetrics.reduce((sum, m) => sum + m.quizCompletions * 60, 0); // Estimate
    const totalDebateSeconds = articleMetrics.reduce((sum, m) => sum + m.debateParticipants * 120, 0); // Estimate
    const totalPollSeconds = articleMetrics.reduce((sum, m) => sum + m.pollAnswers * 30, 0); // Estimate
    
    const passiveSeconds = totalAttentionSeconds - totalKaiSeconds - totalQuizSeconds - totalDebateSeconds - totalPollSeconds;
    
    const averageAttention = totalReaders > 0 ? totalAttentionSeconds / totalReaders : 0;
    const totalTAV = articleMetrics.reduce((sum, m) => sum + m.totalTAV, 0);

    const heatmapData = generateHeatmapData(articleMetrics);

    const report: AttentionReport = {
      id: crypto.randomUUID(),
      name,
      interviewIds,
      dateRangeStart: startDate.toISOString(),
      dateRangeEnd: endDate.toISOString(),
      metrics: {
        totalReaders,
        averageAttentionSeconds: averageAttention,
        totalAttentionHours: totalAttentionSeconds / 3600,
        averageTAV: totalReaders > 0 ? totalTAV / totalReaders : 0,
        comparisonMultiplier: calculateComparisonMultiplier(averageAttention),
        engagementDistribution: {
          passive: passiveSeconds,
          kai: totalKaiSeconds,
          quiz: totalQuizSeconds,
          debate: totalDebateSeconds,
          poll: totalPollSeconds,
        },
      },
      heatmapData,
      kaiInsights,
      articleBreakdown: articleMetrics,
      createdAt: new Date().toISOString(),
    };

    // Save to database
    const insertData: AttentionReportsInsert = {
      id: report.id,
      name: report.name,
      interview_ids: report.interviewIds,
      date_range_start: startDate.toISOString().split('T')[0],
      date_range_end: endDate.toISOString().split('T')[0],
      metrics_snapshot: JSON.parse(JSON.stringify(report.metrics)),
      heatmap_data: JSON.parse(JSON.stringify(report.heatmapData)),
    };
    const { error: saveError } = await supabase
      .from('attention_reports')
      .insert(insertData);

    if (saveError) {
      console.error('Error saving attention report:', saveError);
      // Still return the report even if save fails
    }

    return report;
  } catch (error) {
    console.error('Error generating attention report:', error);
    return null;
  }
}

/**
 * Get saved reports
 */
export async function getSavedReports(): Promise<Array<{
  id: string;
  name: string;
  dateRangeStart: string;
  dateRangeEnd: string;
  createdAt: string;
}>> {
  try {
    const { data, error } = await supabase
      .from('attention_reports')
      .select('id, name, date_range_start, date_range_end, created_at')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching saved reports:', error);
      return [];
    }

    return (data || []).map(r => ({
      id: r.id,
      name: r.name,
      dateRangeStart: r.date_range_start,
      dateRangeEnd: r.date_range_end,
      createdAt: r.created_at,
    }));
  } catch (error) {
    console.error('Error in getSavedReports:', error);
    return [];
  }
}

/**
 * Get a saved report by ID
 */
export async function getReportById(reportId: string): Promise<AttentionReport | null> {
  try {
    const { data, error } = await supabase
      .from('attention_reports')
      .select('*')
      .eq('id', reportId)
      .single();

    if (error) {
      console.error('Error fetching report:', error);
      return null;
    }

    if (!data) return null;

    // Reconstruct report object
    const metrics = data.metrics_snapshot as unknown as AttentionReport['metrics'];
    const heatmapData = (data.heatmap_data as unknown as HeatmapData[]) || [];

    return {
      id: data.id,
      name: data.name,
      interviewIds: data.interview_ids,
      dateRangeStart: data.date_range_start,
      dateRangeEnd: data.date_range_end,
      metrics,
      heatmapData,
      kaiInsights: [], // Would need to re-fetch
      articleBreakdown: [], // Would need to re-fetch
      createdAt: data.created_at,
    };
  } catch (error) {
    console.error('Error in getReportById:', error);
    return null;
  }
}
